import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import type { Tool } from "@repo/app-types/entities/tool";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { SmallTimeStamp } from "@repo/ui/SmallTimeStamp/smallTimeStamp";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { IconPlus } from "@tabler/icons-react";
import { type FC, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useAllToolsQuery } from "../api/entities/tool.js";
import { appPaths } from "../app/appPaths.js";
import { PageBody, PageLayout } from "../components/layout/pageLayout.js";
import { CreateToolModal } from "../features/tools/createToolModal.js";
import { Can } from "../helpers/teammateAuthorizationContext.js";

export const ToolsPage: FC = () => {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { data: tools, isLoading, isError } = useAllToolsQuery();

  const columns: readonly EntityTableColumnDefinition<Tool>[] = useMemo(
    () => [
      {
        id: "name",
        isRowHeader: true,
        header: "Tool name",
        className: "w-[60%]",
        renderCell: (tool) => {
          return (
            <div className="flex items-center">
              <span className="w-0 flex-auto truncate font-medium">
                {tool.name}
              </span>
            </div>
          );
        },
      },
      {
        id: "type",
        header: "Type",
        className: "w-[20%]",
        renderCell: (tool) => {
          return (
            <div className="flex items-center">
              <span className="w-0 flex-auto truncate text-foreground">
                {tool.type.replace(/_/g, " ")}
              </span>
            </div>
          );
        },
      },
      {
        id: "updated_at",
        header: "Updated",
        className: "w-[20%]",
        renderCell: (tool) => {
          const timestamp = tool.updated_at || tool.created_at;
          return (
            <div className="flex items-center">
              <SmallTimeStamp timestamp={timestamp} className="text-sm" />
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Standard}
        title="Tools"
        actions={
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.TEAMMATES}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                size={Size.SMALL}
                variant={ButtonVariant.Primary}
                onClick={() => setIsCreateModalOpen(true)}
                icon={<IconPlus className="h-4 w-4" />}
                isDisabled={!allowed}
              >
                Create tool
              </Button>
            )}
          </Can>
        }
      />

      <PageBody width={PageWidth.Standard}>
        <EntityTable
          aria-label="Tools"
          columns={columns}
          entities={tools ?? []}
          isLoading={isLoading}
          isError={isError}
          emptyMessage="No tools created yet."
          onEntityAction={(tool) => {
            navigate(appPaths.agentTool.build({ toolId: tool.id }));
          }}
        />
      </PageBody>

      <CreateToolModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </PageLayout>
  );
};
